import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { Database } from "@/types/database";
import type {
  AccountInput,
  AccountLedger,
  BookAssignment,
  ClassFilter,
  ClassInput,
  ClassTrialBalanceRow,
  CloseEvent,
  DraftLine,
  EntryPayload,
  ImportAccount,
  ImportResult,
  JournalFilters,
  JournalPage,
  LedgerAccount,
  LedgerClass,
  LedgerEntry,
  LedgerLine,
  TrialBalanceRow,
} from "./types";
import { UNASSIGNED } from "./types";

export const JOURNAL_PAGE_SIZE = 50;
export const CLOSE_EVENT_PAGE_SIZE = 50;

type RelayFunctions = Database["relay"]["Functions"];

/** A ledger function in the generated schema (src/types/database.ts, regenerated from the applied database). */
export type LedgerFunction = Extract<keyof RelayFunctions, `ledger_${string}`>;
/** A ledger table in the generated schema. */
export type LedgerTable = Extract<keyof Database["relay"]["Tables"], `ledger_${string}`>;
/**
 * A function's parameters by their generated names, so a renamed or misspelled parameter
 * fails to compile. Values stay open: the generator types SQL defaults and nullable
 * parameters as optional but non-null, while these functions take null (p_through,
 * p_class_id, p_override_reason), so each wrapper below types its own inputs.
 */
export type LedgerArgs<F extends LedgerFunction> = { [K in keyof RelayFunctions[F]["Args"]]: unknown };

/** What each function returns, typed locally: the generated schema types jsonb results as Json. */
export interface LedgerResults {
  ledger_book_for_org: BookAssignment | null;
  ledger_journal: JournalPage;
  ledger_trial_balance: TrialBalanceRow[];
  ledger_trial_balance_by_class: ClassTrialBalanceRow[];
  ledger_account_ledger: AccountLedger;
  ledger_create_book: string;
  ledger_import_accounts: ImportResult;
  ledger_upsert_account: string;
  ledger_set_account_active: null;
  ledger_upsert_class: string;
  ledger_assign_org: null;
  ledger_unassign_org: null;
  ledger_save_draft: string;
  ledger_delete_draft: null;
  ledger_post_entry: number;
  ledger_void_entry: string;
  ledger_post_opening_balances: string;
  ledger_close_through: null;
  ledger_reopen: null;
}

// Same cast pattern as features/documents/api.ts: the client is untyped here because
// of the nullable-parameter gap above. Names are still checked against the generated
// schema through LedgerFunction, LedgerTable and LedgerArgs (api.contract.test.ts).
function relay() {
  return (supabase as unknown as SupabaseClient).schema("relay");
}

function table(name: LedgerTable) {
  return relay().from(name);
}

async function rpc<F extends LedgerFunction & keyof LedgerResults>(fn: F, args: LedgerArgs<F>): Promise<LedgerResults[F]> {
  const { data, error } = await relay().rpc(fn, args);
  if (error) throw error;
  return data as LedgerResults[F];
}

export const ledgerKeys = {
  all: ["ledger"] as const,
  assignment: (organizationId: string | null) => ["ledger", "assignment", organizationId] as const,
  accounts: (bookId: string | null) => ["ledger", "accounts", bookId] as const,
  classes: (bookId: string | null) => ["ledger", "classes", bookId] as const,
  journal: (bookId: string | null, filters: JournalFilters, page: number) =>
    ["ledger", "journal", bookId, filters, page] as const,
  entry: (bookId: string | null, entryId: string | null) => ["ledger", "entry", bookId, entryId] as const,
  reversal: (bookId: string | null, entryId: string | null) => ["ledger", "reversal", bookId, entryId] as const,
  trialBalance: (bookId: string | null, asOf: string, classFilter: ClassFilter) =>
    ["ledger", "trial-balance", bookId, asOf, classFilter] as const,
  trialBalanceByClass: (bookId: string | null, asOf: string) => ["ledger", "trial-balance-by-class", bookId, asOf] as const,
  accountLedger: (bookId: string | null, accountId: string | null, from: string, to: string, classFilter: ClassFilter) =>
    ["ledger", "account-ledger", bookId, accountId, from, to, classFilter] as const,
  closeEvents: (bookId: string | null, page = 0) => ["ledger", "close-events", bookId, page] as const,
};

// Reads ------------------------------------------------------------------------

export function fetchAssignment(organizationId: string): Promise<BookAssignment | null> {
  return rpc("ledger_book_for_org", { p_organization_id: organizationId });
}

export async function fetchAccounts(bookId: string): Promise<LedgerAccount[]> {
  const { data, error } = await table("ledger_accounts").select("*").eq("book_id", bookId).order("code");
  if (error) throw error;
  return (data ?? []) as LedgerAccount[];
}

export async function fetchClasses(bookId: string): Promise<LedgerClass[]> {
  const { data, error } = await table("ledger_classes").select("*").eq("book_id", bookId).order("name");
  if (error) throw error;
  return (data ?? []) as LedgerClass[];
}

export function fetchJournal(bookId: string, filters: JournalFilters, page: number): Promise<JournalPage> {
  return rpc("ledger_journal", {
    p_book_id: bookId,
    p_filters: filters,
    p_limit: JOURNAL_PAGE_SIZE,
    p_offset: page * JOURNAL_PAGE_SIZE,
  });
}

export async function fetchEntry(bookId: string, entryId: string): Promise<{ entry: LedgerEntry; lines: LedgerLine[] }> {
  const [entryResult, linesResult] = await Promise.all([
    table("ledger_entries").select("*").eq("book_id", bookId).eq("id", entryId).single(),
    table("ledger_lines").select("*").eq("book_id", bookId).eq("entry_id", entryId).order("line_no"),
  ]);
  if (entryResult.error) throw entryResult.error;
  if (linesResult.error) throw linesResult.error;
  return { entry: entryResult.data as LedgerEntry, lines: (linesResult.data ?? []) as LedgerLine[] };
}

export interface LinkedEntry {
  id: string;
  entry_number: number | null;
}

/** The reversal that voided an entry, with its number for the link, or null. */
export async function fetchReversal(bookId: string, entryId: string): Promise<LinkedEntry | null> {
  const { data, error } = await table("ledger_entries")
    .select("id, entry_number")
    .eq("book_id", bookId)
    .eq("reverses_entry_id", entryId)
    .maybeSingle();
  if (error) throw error;
  return (data as LinkedEntry | null) ?? null;
}

// Spread arguments escape excess-property checks, so the class-filter parameters are
// checked against both functions that take them here instead.
type ClassFilterArgs = Pick<LedgerArgs<"ledger_trial_balance">, "p_class_id" | "p_unassigned">
  & Pick<LedgerArgs<"ledger_account_ledger">, "p_class_id" | "p_unassigned">;

// Unassigned (lines with no class) is p_unassigned; it is sent only when chosen, so the
// call shape for the whole book and for a class is unchanged (spec §2.3a).
function classFilterArgs(classFilter: ClassFilter): ClassFilterArgs {
  return classFilter === UNASSIGNED ? { p_class_id: null, p_unassigned: true } : { p_class_id: classFilter };
}

export function fetchTrialBalance(bookId: string, asOf: string, classFilter: ClassFilter): Promise<TrialBalanceRow[]> {
  return rpc("ledger_trial_balance", { p_book_id: bookId, p_as_of: asOf, ...classFilterArgs(classFilter) });
}

export function fetchTrialBalanceByClass(bookId: string, asOf: string): Promise<ClassTrialBalanceRow[]> {
  return rpc("ledger_trial_balance_by_class", { p_book_id: bookId, p_as_of: asOf });
}

export function fetchAccountLedger(
  bookId: string,
  accountId: string,
  from: string,
  to: string,
  classFilter: ClassFilter,
): Promise<AccountLedger> {
  return rpc("ledger_account_ledger", {
    p_book_id: bookId,
    p_account_id: accountId,
    p_from: from,
    p_to: to,
    ...classFilterArgs(classFilter),
  });
}

export async function fetchCloseEvents(bookId: string, page = 0): Promise<CloseEvent[]> {
  const { data, error } = await table("ledger_close_events")
    .select("*")
    .eq("book_id", bookId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .range(page * CLOSE_EVENT_PAGE_SIZE, (page + 1) * CLOSE_EVENT_PAGE_SIZE - 1);
  if (error) throw error;
  return (data ?? []) as CloseEvent[];
}

// Writes -----------------------------------------------------------------------

export interface CreateBookInput {
  organizationId: string;
  name: string;
  booksStartDate: string;
  fiscalYearStartMonth: number;
  template: "none" | "for_profit_basic";
}

export function createBook(input: CreateBookInput): Promise<string> {
  return rpc("ledger_create_book", {
    p_organization_id: input.organizationId,
    p_name: input.name,
    p_books_start_date: input.booksStartDate,
    p_fiscal_year_start_month: input.fiscalYearStartMonth,
    p_template: input.template,
  });
}

export function importAccounts(bookId: string, accounts: ImportAccount[]): Promise<ImportResult> {
  return rpc("ledger_import_accounts", { p_book_id: bookId, p_accounts: accounts });
}

export function upsertAccount(bookId: string, account: AccountInput): Promise<string> {
  return rpc("ledger_upsert_account", { p_book_id: bookId, p_account: account });
}

export function setAccountActive(accountId: string, active: boolean): Promise<null> {
  return rpc("ledger_set_account_active", { p_account_id: accountId, p_active: active });
}

export function upsertClass(bookId: string, input: ClassInput): Promise<string> {
  return rpc("ledger_upsert_class", { p_book_id: bookId, p_class: input });
}

export function assignOrg(organizationId: string, bookId: string, classId: string | null): Promise<null> {
  return rpc("ledger_assign_org", { p_organization_id: organizationId, p_book_id: bookId, p_class_id: classId });
}

/**
 * Stops recording an organization's activity in another organization's books. Posted lines
 * stay in that book and the class stays there as a label; the organization can then keep
 * its own books or be assigned elsewhere. An admin of either side may call it; the books
 * organization cannot leave its own books (ledger.invalid_input).
 */
export function unassignOrg(organizationId: string): Promise<null> {
  return rpc("ledger_unassign_org", { p_organization_id: organizationId });
}

export function saveDraft(bookId: string, entry: EntryPayload): Promise<string> {
  return rpc("ledger_save_draft", { p_book_id: bookId, p_entry: entry });
}

export function deleteDraft(entryId: string): Promise<null> {
  return rpc("ledger_delete_draft", { p_entry_id: entryId });
}

export function postEntry(entryId: string, overrideReason?: string): Promise<number> {
  return rpc("ledger_post_entry", { p_entry_id: entryId, p_override_reason: overrideReason ?? null });
}

export interface VoidEntryInput {
  entryId: string;
  reason: string;
  reversalDate?: string;
  overrideReason?: string;
}

export function voidEntry(input: VoidEntryInput): Promise<string> {
  return rpc("ledger_void_entry", {
    p_entry_id: input.entryId,
    p_reason: input.reason,
    p_reversal_date: input.reversalDate ?? null,
    p_override_reason: input.overrideReason ?? null,
  });
}

export function postOpeningBalances(bookId: string, lines: DraftLine[], label: string): Promise<string> {
  return rpc("ledger_post_opening_balances", { p_book_id: bookId, p_lines: lines, p_source_label: label });
}

export function closeThrough(bookId: string, through: string, kind: "hard" | "soft"): Promise<null> {
  return rpc("ledger_close_through", { p_book_id: bookId, p_through: through, p_kind: kind });
}

export function reopenBooks(bookId: string, through: string | null, reason: string): Promise<null> {
  return rpc("ledger_reopen", { p_book_id: bookId, p_through: through, p_reason: reason });
}

// Hooks ------------------------------------------------------------------------

export function useCurrentBook(enabled = true) {
  const { organizationId, isLoading: orgLoading } = useOrganization();
  const query = useQuery({
    queryKey: ledgerKeys.assignment(organizationId),
    queryFn: () => fetchAssignment(organizationId as string),
    enabled: enabled && !!organizationId,
  });
  const assignment = query.data ?? null;
  return {
    organizationId,
    assignment,
    bookId: !query.isError && assignment?.can_read ? assignment.book_id : null,
    book: assignment?.book ?? null,
    canWrite: !query.isError && (assignment?.can_write ?? false),
    isLoading: orgLoading || query.isLoading,
    error: query.error,
  };
}

export interface JoinableBook {
  organizationId: string;
  organizationName: string;
  bookId: string;
  bookName: string;
}

/**
 * Books another organization keeps that this organization could record its activity in:
 * organizations other than the current one where the user is an owner or admin, and which
 * keep their own books and the user can write to. Assigning needs admin rights on both
 * sides (ledger_assign_org).
 */
export function useJoinableBooks() {
  const { organizationId, memberships } = useOrganization();
  const candidates = memberships.filter(
    (m) => m.organization_id !== organizationId && (m.role === "owner" || m.role === "admin"),
  );
  const results = useQueries({
    queries: candidates.map((m) => ({
      queryKey: ledgerKeys.assignment(m.organization_id),
      queryFn: () => fetchAssignment(m.organization_id),
    })),
  });
  const data: JoinableBook[] = [];
  results.forEach((result, index) => {
    const assignment = result.data;
    // Joining writes a class into those books, so read-only access there is not enough.
    if (assignment?.is_books_org && assignment.book && assignment.can_write) {
      data.push({
        organizationId: candidates[index].organization_id,
        organizationName: candidates[index].organization.name,
        bookId: assignment.book_id,
        bookName: assignment.book.name,
      });
    }
  });
  // A failed lookup is not "keeps no books": callers tell the two apart before offering nothing.
  return {
    data,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    refetch: () => Promise.all(results.filter((r) => r.isError).map((r) => r.refetch())),
  };
}

/**
 * Each organization's book assignment (ledger_book_for_org), keyed like useCurrentBook so
 * any ledger write refreshes it. `data` holds settled lookups only: null means the
 * organization is not recorded in any books, or not in any this user can see. A lookup
 * that failed is left out rather than read as "not recorded".
 */
export function useOrgAssignments(organizationIds: string[]) {
  const ids = [...new Set(organizationIds)];
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ledgerKeys.assignment(id),
      queryFn: () => fetchAssignment(id),
    })),
  });
  const data: Record<string, BookAssignment | null> = {};
  results.forEach((result, index) => {
    if (!result.isError && result.data !== undefined) data[ids[index]] = result.data;
  });
  return {
    data,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
  };
}

export function useLedgerAccounts(bookId: string | null) {
  return useQuery({
    queryKey: ledgerKeys.accounts(bookId),
    queryFn: () => fetchAccounts(bookId as string),
    enabled: !!bookId,
  });
}

export function useLedgerClasses(bookId: string | null) {
  return useQuery({
    queryKey: ledgerKeys.classes(bookId),
    queryFn: () => fetchClasses(bookId as string),
    enabled: !!bookId,
  });
}

export function useJournal(bookId: string | null, filters: JournalFilters, page: number) {
  return useQuery({
    queryKey: ledgerKeys.journal(bookId, filters, page),
    queryFn: () => fetchJournal(bookId as string, filters, page),
    enabled: !!bookId,
  });
}

export function useLedgerEntry(bookId: string | null, entryId: string | null) {
  return useQuery({
    queryKey: ledgerKeys.entry(bookId, entryId),
    queryFn: () => fetchEntry(bookId as string, entryId as string),
    enabled: !!bookId && !!entryId,
  });
}

export function useReversal(bookId: string | null, entryId: string | null) {
  return useQuery({
    queryKey: ledgerKeys.reversal(bookId, entryId),
    queryFn: () => fetchReversal(bookId as string, entryId as string),
    enabled: !!bookId && !!entryId,
  });
}

export function useTrialBalance(bookId: string | null, asOf: string, classFilter: ClassFilter, enabled = true) {
  return useQuery({
    queryKey: ledgerKeys.trialBalance(bookId, asOf, classFilter),
    queryFn: () => fetchTrialBalance(bookId as string, asOf, classFilter),
    enabled: enabled && !!bookId && !!asOf,
  });
}

export function useTrialBalanceByClass(bookId: string | null, asOf: string, enabled = true) {
  return useQuery({
    queryKey: ledgerKeys.trialBalanceByClass(bookId, asOf),
    queryFn: () => fetchTrialBalanceByClass(bookId as string, asOf),
    enabled: enabled && !!bookId && !!asOf,
  });
}

export function useAccountLedger(
  bookId: string | null,
  accountId: string | null,
  from: string,
  to: string,
  classFilter: ClassFilter,
) {
  return useQuery({
    queryKey: ledgerKeys.accountLedger(bookId, accountId, from, to, classFilter),
    queryFn: () => fetchAccountLedger(bookId as string, accountId as string, from, to, classFilter),
    enabled: !!bookId && !!accountId && !!from && !!to,
  });
}

export function useCloseEvents(bookId: string | null, page = 0) {
  return useQuery({
    queryKey: ledgerKeys.closeEvents(bookId, page),
    queryFn: () => fetchCloseEvents(bookId as string, page),
    enabled: !!bookId,
  });
}

function useLedgerMutation<TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ledgerKeys.all }),
  });
}

export const useCreateBook = () => useLedgerMutation(createBook);
export const useImportAccounts = (bookId: string | null) =>
  useLedgerMutation((accounts: ImportAccount[]) => importAccounts(bookId as string, accounts));
export const useUpsertAccount = (bookId: string | null) =>
  useLedgerMutation((account: AccountInput) => upsertAccount(bookId as string, account));
export const useSetAccountActive = () =>
  useLedgerMutation((input: { accountId: string; active: boolean }) => setAccountActive(input.accountId, input.active));
export const useUpsertClass = (bookId: string | null) =>
  useLedgerMutation((input: ClassInput) => upsertClass(bookId as string, input));
export const useAssignOrg = () =>
  useLedgerMutation((input: { organizationId: string; bookId: string; classId: string | null }) =>
    assignOrg(input.organizationId, input.bookId, input.classId));
/**
 * Unlike useLedgerMutation, this re-reads the books after a refusal as well: ledger.not_found
 * means another admin already stopped it, and the page should catch up rather than keep
 * offering an action that can only fail again.
 */
export function useUnassignOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organizationId: string) => unassignOrg(organizationId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ledgerKeys.all }),
  });
}
export const useSaveDraft = (bookId: string | null) =>
  useLedgerMutation((entry: EntryPayload) => saveDraft(bookId as string, entry));
export const useDeleteDraft = () => useLedgerMutation((entryId: string) => deleteDraft(entryId));
export const usePostEntry = () =>
  useLedgerMutation((input: { entryId: string; overrideReason?: string }) => postEntry(input.entryId, input.overrideReason));
export const useVoidEntry = () => useLedgerMutation(voidEntry);
export const usePostOpeningBalances = (bookId: string | null) =>
  useLedgerMutation((input: { lines: DraftLine[]; label: string }) =>
    postOpeningBalances(bookId as string, input.lines, input.label));
export const useCloseThrough = (bookId: string | null) =>
  useLedgerMutation((input: { through: string; kind: "hard" | "soft" }) =>
    closeThrough(bookId as string, input.through, input.kind));
export const useReopenBooks = (bookId: string | null) =>
  useLedgerMutation((input: { through: string | null; reason: string }) =>
    reopenBooks(bookId as string, input.through, input.reason));
